import { Button, Collapse, Select } from 'antd';
import { Option, SizeVariant, Thumbnail } from 'api';
import SummerwoodSVG from 'components/summerwoodsvg/summerwood-svg';
import { motion } from 'framer-motion';
import { UI } from 'helpers/constants';
import Typography from 'helpers/fonts';
import { getImageUrl } from 'helpers/selectExistingImage';
import { useHover } from 'helpers/useHover';
import { calculateComponentPrice } from 'helpers/pricing';
import { formatMoney } from 'helpers/util';
import React, { RefObject, useState } from 'react';
import { addOption, removeOption } from 'viewer/viewerActions';
import { useAppState } from 'state/appstate/appstate';
import { UpOutlined } from '@ant-design/icons';

export interface TabPaneCardsProps {
  title: string;
  options: Array<Option>;
}

interface TabPaneCardProps {
  onClick: (orderCode?: string) => void;
  onRemoveClick?: () => void;
  title: string;
  boxSize: string | undefined;
  sizeVariants?: Array<SizeVariant>;
  itemCode: string;
  price: number;
  img: string;
  priceType: Option.priceType;
}

const TabPaneCard: React.FunctionComponent<TabPaneCardProps> = ({
  title,
  boxSize,
  sizeVariants,
  itemCode,
  price,
  onClick,
  onRemoveClick,
  img,
  priceType,
}: TabPaneCardProps) => {
  const [hoverRef, isHovered] = useHover();
  const [selectedVariantCode, setSelectedVariantCode] = useState<string>(itemCode);
  const defaultSizeVariant: SizeVariant = { orderCode: itemCode, boxSize: boxSize ?? '' };

  return (
    <div
      ref={hoverRef as RefObject<HTMLDivElement>}
      style={{
        maxHeight: '220px',
        background: Theme['@white'],
        borderRadius: '5px',
        display: 'flex',
        alignContent: 'center',
        padding: '8px',
        border: '1px solid',
        borderColor: isHovered ? Theme['@primary-color'] : 'transparent',
      }}
    >
      <div
        style={{
          justifyContent: 'center',
          alignContent: 'center',
          alignItems: 'center',
          display: 'flex',
          width: '25%',
          borderRight: `1px solid ${Theme['@grey-normal']}`,
        }}
      >
        <img
          style={{
            maxWidth: '100%',
            padding: '0 8px',
          }}
          alt=""
          src={img}
        />
      </div>
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          width: '75%',
          height: '100%',
          padding: '0px 8px 0 16px',
          justifyContent: 'space-between',
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            marginBottom: '0.5rem',
          }}
        >
          <Typography
            styleid={10}
            style={{
              fontWeight: 500,
              maxWidth: '100%',
            }}
          >
            {title}
          </Typography>

          <Typography
            style={{ color: Theme['@grey-dark'], marginTop: '0.5rem' }}
            styleid={9}
          >
            Item Code: {itemCode}
          </Typography>
          {boxSize ? (
            <Typography style={{ color: Theme['@grey-dark'] }} styleid={9}>
              Box Size: {boxSize}
            </Typography>
          ) : (
            <></>
          )}
          {sizeVariants?.length ? (
            <Typography style={{ marginTop: 8 }} styleid={9}>
              <div style={{display: 'flex', alignItems: 'center', gap: 4, marginRight: '2rem'}}>
                <span>Size: </span>
                <Select
                  value={selectedVariantCode}
                  style={{ width: '100%' }}
                  onSelect={setSelectedVariantCode}
                >
                  {[defaultSizeVariant, ...sizeVariants].map((variant) => (
                    <Select.Option key={variant.orderCode} value={variant.orderCode}>
                      <Typography styleid={6}>{variant.boxSize}</Typography>
                    </Select.Option>
                  ))}
                </Select>
              </div>
            </Typography>
          ) : (
            <></>
          )}
        </div>

        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Typography styleid={9} style={{ color: Theme['@black'] }}>
            {price <= 0 ? 'Cost Included' : `${formatMoney(price)}${priceType === Option.priceType.FIXED ? '' : '/' + priceType}`}
          </Typography>
          {!onRemoveClick && (<Button
            type="link"
            onClick={() => onClick(selectedVariantCode)}
            style={{ padding: 0, height: 22 }}
          >
            <SummerwoodSVG type="plusButton" style={{ height: 18 }} />
          </Button>)}
          {onRemoveClick && (<Button
            type="link"
            onClick={onRemoveClick}
            style={{ padding: 0, height: 22 }}
          >
            <SummerwoodSVG type="minusButton" style={{ height: 18 }} />
          </Button>)}
        </div>
      </div>
    </div>
  );
};

const TabPaneCards: React.FunctionComponent<TabPaneCardsProps> = ({
  title,
  options,
}: TabPaneCardsProps) => {
  const itemVariants = {
    show: {
      y: 0,
      opacity: 1,
      transition: {
        y: {
          stiffness: 1000,
          velocity: -100,
          ease: UI.EASE_QUINT,
          duration: 0.4,
        },
      },
    },
    hidden: {
      y: 500,
      opacity: 0,
      transition: {
        y: { stiffness: 1000 },
      },
    },
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.07,
      },
    },
  };

  const { uState } = useAppState();
  const { bomOnlyOptions = [] } = uState ?? {};
  const width = uState?.dimensions?.width || 0;
  const depth = uState?.dimensions?.depth || 0;

  // Check if 8x8 post upgrade (WRCP6688) is active
  const allOptions = uState?.options ?? [];
  const hasWRCP6688 = bomOnlyOptions.some(
    (opt) => allOptions.find((o) => o.id === opt.definitionId)?.orderCode === 'WRCP6688'
  );

  const optionCards = options.map((o): Option & { instanceId: string | undefined } => {
    const bomOnlyOption = bomOnlyOptions.find(
      ({ definitionId }) => o.id === definitionId
    );
    return { ...o, instanceId: bomOnlyOption?.id };
  }).map((o) => {
    const effectivePrice = o.components?.length
      ? calculateComponentPrice(o.components, width, depth, hasWRCP6688)
      : o.pricePerUnit;

    return (
    <motion.span variants={itemVariants} key={o.id} style={{ margin: '6px 0' }}>
      <TabPaneCard
        key={o.id}
        price={effectivePrice}
        priceType={o.priceType}
        title={o.title}
        boxSize={o.boxSize}
        sizeVariants={o.sizeVariants}
        itemCode={o.orderCode}
        img={getImageUrl(o.thumbnail as Thumbnail)}
        onClick={(orderCode) => addOption(o.optionTypeId, orderCode ?? o.orderCode)}
        onRemoveClick={o.instanceId ? () => removeOption(o.instanceId!) : undefined}
      />
    </motion.span>
    );
  });

  return title ? (
    <Collapse
      ghost
      expandIconPosition='right'
      expandIcon={({ isActive }) => <UpOutlined  rotate={isActive ? 0 : 180} />}
    >
      <Collapse.Panel
        header={
          title && (
            <Typography
              styleid={10}
              style={{ fontWeight: 500, paddingTop: '1rem' }}
            >
              {title}
            </Typography>
          )
        }
        key={title}
      >
        <motion.div
          style={{
            display: 'grid',
            gridAutoColumns: '1fr',
            gridAutoRows: 'min-content',
          }}
          variants={containerVariants}
          initial="hidden"
          animate="show"
        >
          {optionCards}
        </motion.div>
      </Collapse.Panel>
    </Collapse>
  ) : (
    <motion.div
      style={{
        display: 'grid',
        gridAutoColumns: '1fr',
        gridAutoRows: 'min-content',
        padding: '16px'
      }}
      variants={containerVariants}
      initial="hidden"
      animate="show"
    >
      {title && (
        <Typography styleid={10} style={{ fontWeight: 500, paddingTop: '1rem' }}>
          {title}
        </Typography>
      )}
      {optionCards}
    </motion.div>
  );
};

export default TabPaneCards;
